import React, { useEffect, useState } from "react";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import { FiMapPin, FiHash, FiFlag } from "react-icons/fi";

export type UpsertAirport = {
  airportName: string;
  airportCode: string;
  cityName?: string;
  cityCode?: string;
  countryName?: string;
  countryCode?: string;
};

export default function CreateAirport({
  onSubmit,
  onCancel,
  defaultValues,
}: {
  onSubmit: (payload: UpsertAirport) => void;
  onCancel?: () => void;
  defaultValues?: Partial<UpsertAirport>;
}) {
  const [form, setForm] = useState<UpsertAirport>({
    airportName: defaultValues?.airportName ?? "",
    airportCode: defaultValues?.airportCode ?? "",
    cityName: defaultValues?.cityName ?? "",
    cityCode: defaultValues?.cityCode ?? "",
    countryName: defaultValues?.countryName ?? "",
    countryCode: defaultValues?.countryCode ?? "",
  });

  useEffect(() => {
    setForm({
      airportName: defaultValues?.airportName ?? "",
      airportCode: defaultValues?.airportCode ?? "",
      cityName: defaultValues?.cityName ?? "",
      cityCode: defaultValues?.cityCode ?? "",
      countryName: defaultValues?.countryName ?? "",
      countryCode: defaultValues?.countryCode ?? "",
    });
  }, [
    defaultValues?.airportName,
    defaultValues?.airportCode,
    defaultValues?.cityName,
    defaultValues?.cityCode,
    defaultValues?.countryName,
    defaultValues?.countryCode,
  ]);

  const change = <K extends keyof UpsertAirport>(k: K, v: UpsertAirport[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const airportName = (form.airportName ?? "").trim();
    const airportCode = (form.airportCode ?? "").trim().toUpperCase();
    if (!airportName || !airportCode) return;

    onSubmit({
      airportName,
      airportCode,
      cityName: form.cityName?.trim() || "",
      cityCode: form.cityCode?.trim().toUpperCase() || "",
      countryName: form.countryName?.trim() || "",
      countryCode: form.countryCode?.trim().toUpperCase() || "",
    });
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label="Airport name" icon={<FiMapPin />}>
        <Input value={form.airportName} onChange={(e) => change("airportName", e.target.value)} placeholder="e.g., Dubai International" required />
      </Field>
      <Field label="Airport code" icon={<FiHash />}>
        <Input value={form.airportCode} onChange={(e) => change("airportCode", e.target.value)} placeholder="e.g., DXB" required />
      </Field>
      <Field label="City name" icon={<FiMapPin />}>
        <Input value={form.cityName ?? ""} onChange={(e) => change("cityName", e.target.value)} placeholder="e.g., Dubai" />
      </Field>
      <Field label="City code" icon={<FiHash />}>
        <Input value={form.cityCode ?? ""} onChange={(e) => change("cityCode", e.target.value)} placeholder="e.g., DXB" />
      </Field>
      <Field label="Country name" icon={<FiFlag />}>
        <Input value={form.countryName ?? ""} onChange={(e) => change("countryName", e.target.value)} placeholder="e.g., United Arab Emirates" />
      </Field>
      <Field label="Country code" icon={<FiHash />}>
        <Input value={form.countryCode ?? ""} onChange={(e) => change("countryCode", e.target.value)} placeholder="e.g., AE" />
      </Field>

      <div className="mt-2 flex justify-end gap-2 sm:col-span-2">
        <button type="button" className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => onCancel?.()}>Cancel</button>
        <button type="submit" className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700">Create Airport</button>
      </div>
    </form>
  );
}
